// ─── Friendly Entity URLs (ADR 0012, Phase 6.5) ───
//
// Space/List URLs are `/app/s/{slug}-{short}` and `/app/l/{slug}-{short}`.
// - `slug` is a cosmetic, kebab-cased form of the entity name (renames self-heal
//   via a canonical redirect; a stale slug still resolves because `short` is
//   authoritative).
// - `short` is the last 6 hex chars of the entity's dashless UUID — stable across
//   renames, effectively collision-free at single-tenant scale (16^6 = 16.7M).
//
// These are PURE functions shared by the API (canonical-path building on resolve)
// and the web app (link builders + route-ref parsing). No I/O, no scope logic.

const SLUG_MAX = 40;
const SLUG_FALLBACK = 'untitled';

/** Last 6 hex chars of a UUID, dashless + lowercased. Stable across renames. */
export function shortId(uuid: string): string {
  return uuid.replace(/-/g, '').toLowerCase().slice(-6);
}

/**
 * Kebab-case an entity name for the cosmetic slug segment. NFKD-normalizes and
 * strips diacritics so accented names produce clean ASCII slugs; collapses every
 * run of non-[a-z0-9] to a single `-`; caps length; empty/symbol-only → 'untitled'.
 * Cosmetic ONLY — never used for resolution (that's `short`).
 */
export function slugify(name: string): string {
  const base = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX)
    .replace(/-+$/g, ''); // re-trim in case the cap landed mid-dash
  return base.length > 0 ? base : SLUG_FALLBACK;
}

/** `{slug}-{short}` — the ref segment carried by a friendly route param. */
export function buildEntityRef(name: string, uuid: string): string {
  return `${slugify(name)}-${shortId(uuid)}`;
}

/** Canonical friendly path for a Space. */
export function spacePath(name: string, uuid: string): string {
  return `/app/s/${buildEntityRef(name, uuid)}`;
}

/**
 * Canonical friendly path for a Project (ADR 0039 D9, GHE #1200) — `/app/p/{code}-{short}`.
 *
 * The slug is the project CODE, not its name: codes are short, upper-case identifiers people
 * already say out loud ("ACME-ERP"), so the URL reads as the thing it opens. Cosmetic like every
 * slug here; the short id is what resolves.
 */
export function projectPath(code: string, uuid: string): string {
  return `/app/p/${buildEntityRef(code, uuid)}`;
}

/** Canonical friendly path for a List. */
export function listPath(name: string, uuid: string): string {
  return `/app/l/${buildEntityRef(name, uuid)}`;
}

/**
 * Canonical friendly path for a Dashboard (GHE #315).
 *
 * Unlike Space/List this keeps the ORIGINAL `/app/dashboards/` prefix rather than adding a
 * terse `/app/d/` sibling, so a single route param accepts either shape and every already-
 * bookmarked UUID link keeps resolving with no redirect table. Dashboard links are read far
 * more often than they are typed, so readability beats brevity here.
 *
 * Resolution is NOT via `GET /resolve` (which is space-scoped: its `ResolvedEntity` requires
 * a non-null `spaceId`+`workspaceId`, and a GLOBAL dashboard has neither). Dashboards resolve
 * through `GET /dashboards/resolve`, which reuses their own owner/isDefault/isShared read
 * gate — see `DashboardService.resolveByShort`.
 */
export function dashboardPath(name: string, uuid: string): string {
  return `/app/dashboards/${buildEntityRef(name, uuid)}`;
}

/**
 * Canonical friendly path for a CLIENT inside DevOps · Valoraciones (GHE #1125).
 *
 * `/app/devops/{slug}-{short}` en vez de `/app/devops?cliente=<uuid>`. Petición del dueño
 * 2026-09-14: la URL con GUID es ilegible y no dice a dónde lleva antes de pulsarla.
 *
 * 🚨 El `short` sale del id de PLANI, no del de Dataverse. Los dos son GUID y confundirlos NO da
 * error: da la pantalla de otro cliente, o una lista vacía que se lee como «este cliente no tiene
 * nada valorado». Es el mismo aviso que ya lleva la ruta, y por eso el parámetro se llama
 * `clienteRef` y no `ref` a secas.
 *
 * ⚠️ A diferencia de espacios y listas, esto NO resuelve contra `GET /resolve`: la pantalla ya
 * tiene cargada la lista entera de clientes de la empresa (el tope del esquema son 100), así que
 * casar el `short` es una búsqueda en memoria y no una llamada más. Si algún día esa lista deja
 * de venir entera, esta resolución deja de ser correcta — y sería en silencio.
 */
export function clientPath(name: string, uuid: string): string {
  return `/app/devops/${buildEntityRef(name, uuid)}`;
}

/**
 * Shareable friendly path for a Task. A task has no page of its own — the
 * `/app/t/$taskRef` route resolves + redirects to the task's list with the
 * detail panel open (?openTask=). This builder produces the shareable form
 * (e.g. a "copy link" affordance); in-app navigation still goes direct to the
 * list route + ?openTask to avoid a resolve round-trip (ADR 0012).
 */
export function taskPath(name: string, uuid: string): string {
  return `/app/t/${buildEntityRef(name, uuid)}`;
}

/**
 * Parse a friendly route ref into `{ slug, short }`. `short` is the trailing
 * 6-hex group; `slug` is everything before the final `-`. Returns null when there
 * is no valid trailing 6-hex short id (caller should render not-found).
 */
export function parseEntityRef(ref: string): { slug: string; short: string } | null {
  const match = /^(.*)-([0-9a-f]{6})$/i.exec(ref);
  const slug = match?.[1];
  const short = match?.[2];
  if (slug === undefined || short === undefined) {
    return null;
  }
  return { slug, short: short.toLowerCase() };
}
